import { Link, useLocation, useNavigate } from 'react-router-dom';
import { SidebarFlyout } from '@/components/layout/SidebarFlyout';
import {
  LayoutDashboard,
  FolderOpen,
  AlertTriangle,
  Crosshair,
  ChevronRight,
  Plug,
  Users,
  Bell,
  ClipboardList,
  Settings,
  BarChart2,
  ListTodo,
  ShieldCheck,
  Timer,
  Crown,
  Sparkles,
  Zap,
  Star,
  Lock,
  Kanban,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/config/routes';
import {
  ICON_ACTIVE,
  ICON_DEFAULT,
  NAV_ROW,
  NAV_SUB_LIST,
  NAV_SUB_ROW,
  navItemState,
} from '@/components/layout/navStyles';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Briefcase, Building2 } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useCmsPermissions } from '../hooks/useCmsPermissions';
import { useCurrentPlan } from '../hooks/useCms';
import { type CurrentPlanResponse, cmsService } from '../services/cmsService';
import { notificationsService } from '@/services/notificationsService';
import { useNotificationSocket } from '@/hooks/useNotificationSocket';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const IS_CMS_BUNDLE = import.meta.env.VITE_APP_VARIANT === 'cms';


interface ToolItem {
  /** Stable key — the code below checks `tool.name === 'CMS'`. */
  name: string;
  /** What the sidebar shows; falls back to `name`. */
  label?: string;
  href: string;
  icon: React.ElementType;
  subItems?: Array<{ name: string; href: string; icon: React.ElementType }>;
}

const tools: ToolItem[] = [
  {
    name: 'CMS', // Case Management System
    label: 'CMS',
    href: ROUTES.CMS_DASHBOARD,
    icon: LayoutDashboard,
    subItems: [
      { name: 'Dashboard',       href: ROUTES.CMS_DASHBOARD,       icon: LayoutDashboard },
      { name: 'Cases',           href: ROUTES.CMS_CASES,           icon: FolderOpen },
      { name: 'Case Board',      href: ROUTES.CMS_CASE_BOARD,      icon: Kanban },
      { name: 'Alert Queue',     href: ROUTES.CMS_ALERTS,          icon: AlertTriangle },
      { name: 'App Integrations', href: ROUTES.CMS_ALERT_SOURCES, icon: Plug },
      { name: 'Team Management', href: ROUTES.CMS_TEAM,            icon: Users },
      { name: 'Notifications',   href: ROUTES.CMS_NOTIFICATIONS,   icon: Bell },
      { name: 'My Metrics',      href: ROUTES.CMS_METRICS,         icon: BarChart2 },
      { name: 'Audit Logs',      href: ROUTES.CMS_AUDIT,           icon: ClipboardList },
      { name: 'Settings & Plan', href: ROUTES.CMS_SETTINGS,        icon: Settings },
      { name: 'CERT Collaboration', href: ROUTES.CMS_CERT_COLLABORATION, icon: ShieldCheck },
      { name: 'Tenants',         href: ROUTES.CMS_TENANTS,         icon: Building2 },
      { name: 'Customers',       href: ROUTES.CMS_CUSTOMERS,       icon: Briefcase },
    ],
  },
];

// ── Plan badge helpers ────────────────────────────────────────────────────────

function formatDaysLeft(days: number): string {
  if (days <= 0) return 'Expired';
  if (days === 1) return '1d left';
  if (days < 30) return `${Math.ceil(days)}d left`;
  const months = Math.floor(days / 30.44);
  if (months < 12) return `${months}mo left`;
  const years = Math.floor(months / 12);
  const remMonths = months % 12;
  return remMonths > 0 ? `${years}y ${remMonths}mo left` : `${years}yr left`;
}

interface PlanBadgeConfig {
  label: string;
  icon: React.ElementType;
  className: string;
  dotColor: string;
  pulse?: boolean;
}

function getPlanBadgeConfig(plan: CurrentPlanResponse | null): PlanBadgeConfig | null {
  if (!plan) return null;
  const planName = plan.plan?.name;
  const trial = plan.trial;
  const pending = plan.upgradeRequest?.status === 'pending';

  if (pending) {
    return {
      label: 'Pending',
      icon: Timer,
      className: 'bg-amber-500/15 text-amber-600 border border-amber-500/30',
      dotColor: 'bg-amber-500',
      pulse: true,
    };
  }

  // Trial not yet activated by user — show an "Activate" prompt badge
  if (planName === 'free_trial' && trial?.trialActivated === false) {
    return {
      label: 'Activate',
      icon: Star,
      className: 'bg-violet-500/15 text-violet-500 border border-violet-500/25',
      dotColor: 'bg-violet-500',
      pulse: true,
    };
  }

  // Active free trial with live countdown
  if (trial?.isTrial) {
    const days = Math.ceil(trial.daysRemaining ?? 0);
    const urgent = days <= 3;
    const warning = days <= 7;
    return {
      label: `Trial · ${formatDaysLeft(days)}`,
      icon: Timer,
      className: urgent
        ? 'bg-red-500/15 text-red-500 border border-red-500/40'
        : warning
        ? 'bg-orange-500/15 text-orange-500 border border-orange-500/30'
        : 'bg-amber-500/15 text-amber-500 border border-amber-500/25',
      dotColor: urgent ? 'bg-red-500' : warning ? 'bg-orange-400' : 'bg-amber-400',
      pulse: warning,
    };
  }

  // Paid plans — optionally show expiry if the API provides it
  const orgExpiresAt: string | null =
    (plan.organization?.planExpiresAt as string | null) ??
    (plan.organization?.subscriptionEndDate as string | null) ??
    null;
  let timeLabel = '';
  if (orgExpiresAt) {
    const daysLeft = (new Date(orgExpiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
    timeLabel = ` · ${formatDaysLeft(daysLeft)}`;
  }

  const MAP: Record<string, PlanBadgeConfig> = {
    starter: {
      label: `Starter${timeLabel}`,
      icon: Zap,
      className: 'bg-blue-500/15 text-blue-500 border border-blue-500/25',
      dotColor: 'bg-blue-500',
    },
    professional: {
      label: `Pro ✦${timeLabel}`,
      icon: Sparkles,
      className: 'bg-violet-500/15 text-violet-500 border border-violet-500/25',
      dotColor: 'bg-violet-500',
    },
    enterprise: {
      label: `Enterprise${timeLabel}`,
      icon: Crown,
      className: 'bg-amber-500/15 text-amber-600 border border-amber-500/25',
      dotColor: 'bg-amber-500',
    },
  };

  return MAP[planName ?? ''] ?? null;
}

interface CmsSidebarProps {
  collapsed: boolean;
}

export function CmsSidebar({ collapsed }: CmsSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasRole } = useAuthStore();
  const [cmsExpanded, setCmsExpanded] = useState(true);
  const permissions = useCmsPermissions();

  // Same badge count source as the shared Header bell (notification-service),
  // so both stay consistent now that CMS notifications live there too.
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const loadUnreadCount = useCallback(() => {
    notificationsService
      .count()
      .then((res) => setUnreadNotifCount(res.unreadCount ?? 0))
      .catch(() => {});
  }, []);
  useEffect(() => { loadUnreadCount(); }, [loadUnreadCount]);
  useNotificationSocket({
    onUnreadCount: (count) => setUnreadNotifCount(count),
    onConnect: () => loadUnreadCount(),
  });

  const { plan: currentPlan, loading: planLoading } = useCurrentPlan();
  const planBadge = getPlanBadgeConfig(currentPlan);

  const isPlatformAdmin = hasRole('platform_admin') || permissions.role === 'platform_admin';

  const trialNotActivated =
    !isPlatformAdmin &&
    currentPlan?.plan?.name === 'free_trial' &&
    currentPlan?.trial?.trialActivated === false;

  const [isTrialModalOpen, setIsTrialModalOpen] = useState(false);
  const [activatingTrial, setActivatingTrial] = useState(false);

  // Auto-show the discovery popup once per login session for unactivated users.
  // sessionStorage is cleared on logout / tab close, so every new login shows it.
  // "Remind me later" sets the session key, preventing re-trigger during the same session.
  // Clicking CMS in the sidebar can still re-open it manually.
  useEffect(() => {
    if (!trialNotActivated || planLoading) return;
    const sessionKey = 'cms_trial_popup_shown';
    if (!sessionStorage.getItem(sessionKey)) {
      sessionStorage.setItem(sessionKey, '1');
      setIsTrialModalOpen(true);
    }
  }, [trialNotActivated, planLoading]);

  const handleActivateTrial = async () => {
    setActivatingTrial(true);
    try {
      await cmsService.activateTrial();
      setIsTrialModalOpen(false);
      // Full page reload to /cms/dashboard — CmsTrialGuard will now allow access
      window.location.assign(ROUTES.CMS_DASHBOARD);
    } catch (err) {
      console.error('Failed to activate trial:', err);
      setActivatingTrial(false);
    }
  };

  const isTier1 = permissions.role === 'tier1';
  const isTier2 = permissions.role === 'tier2';
  const isTier3 = permissions.role === 'tier3';

  const caseItemLabel = (name: string) => {
    if (name !== 'Cases') return name;
    if (isTier1) return 'My Cases';
    if (isTier2 || isTier3) return 'Cases';
    return name;
  };

  const activeItemRef = useRef<HTMLAnchorElement>(null);
  const cmsButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (location.pathname.startsWith('/cms')) {
      setCmsExpanded(true);
    }
  }, [location.pathname]);

  // Ensure the active CMS menu item smoothly scrolls into view in the sidebar
  useEffect(() => {
    if (location.pathname.startsWith('/cms')) {
      const timer = setTimeout(() => {
        const target = activeItemRef.current || cmsButtonRef.current;
        if (target) {
          target.scrollIntoView({
            block: 'nearest',
            inline: 'nearest',
            behavior: 'smooth',
          });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, location.search, cmsExpanded]);

  const filteredTools = tools.filter(() => true);

  const filterSubItems = (subItems?: typeof tools[0]['subItems']) => {
    if (!subItems) return [];
    return subItems.filter((sub) => {
      // Before the platform_admin short-circuit, not after: this is the one
      // entry that is hidden *from* tenants rather than shown to them, and the
      // blanket `return true` below would otherwise reveal it to everyone.
      //
      // Bundle-gated as well, because this sidebar is mounted inside the
      // platform's own Sidebar too, and the platform router has no
      // /cms/tenants route — the link would go nowhere. In platform mode
      // organizations are administered through UMS at /admin/organizations.
      if (sub.name === 'Tenants') return IS_CMS_BUNDLE && isPlatformAdmin;
      // Kibo's own customer register: routed in both bundles, platform_admin
      // only. An on-premise install has no platform_admin, and its backend
      // does not serve the vendor endpoints even if one were created.
      if (sub.name === 'Customers') return isPlatformAdmin;

      // Team Management, Notifications and Audit Logs now just redirect to the
      // platform's own pages in embedded mode (same backend, same data) — kept
      // in the `tools` array above rather than deleted, since they're the only
      // nav to reach this content at all in the standalone/on-prem bundle,
      // where this sidebar is the entire app. Hidden here, not removed, so
      // embedded mode doesn't show a menu item whose only job is to leave CMS.
      if (
        (sub.name === 'Team Management' || sub.name === 'Notifications' || sub.name === 'Audit Logs') &&
        !IS_CMS_BUNDLE
      ) {
        return false;
      }

      if (isPlatformAdmin) {
        return true;
      }
      if (sub.name === 'Case Board')          return permissions.canViewCaseBoard;
      if (sub.name === 'App Integrations')     return permissions.canViewAlertSources;
      if (sub.name === 'Team Management')     return permissions.canViewTeamManagement;
      if (sub.name === 'Audit Logs')          return permissions.canViewAuditLog;
      if (sub.name === 'Settings & Plan')     return permissions.canViewSettingsAndPlan;
      if (sub.name === 'My Metrics')          return permissions.canViewPersonalMetrics;
      if (sub.name === 'CERT Collaboration')  return permissions.canReviewCertRequests;
      return true;
    });
  };

  if (collapsed) {
    return (
      <div className="flex flex-col gap-2 items-center pt-2">
        <div className="w-8 border-t border-sidebar-border/60 my-1" />
        {filteredTools.map((tool) => {
          const Icon = tool.icon;
          const isCmsActive = tool.name === 'CMS' && location.pathname.startsWith('/cms');
          const isActive = isCmsActive || (!isCmsActive && location.pathname.startsWith(tool.href));
          const subItemsList = filterSubItems(tool.subItems);

          if (tool.name === 'CMS') {
            const flyoutSubItems = subItemsList.map((sub) => {
              const isSubActive = sub.href.includes('?')
                ? (location.pathname === sub.href.split('?')[0] && location.search.includes(sub.href.split('?')[1]))
                : (sub.href === ROUTES.CMS_DASHBOARD
                    ? (location.pathname === ROUTES.CMS_DASHBOARD && !location.search.includes('tab=analytics'))
                    : (sub.href === ROUTES.CMS_CASE_BOARD
                        ? (location.pathname === ROUTES.CMS_CASE_BOARD)
                        : (location.pathname === sub.href || (sub.href === ROUTES.CMS_CASES && location.pathname.startsWith('/cms/cases/') && location.pathname !== ROUTES.CMS_CASE_BOARD))));
              const isNotifItem = sub.href === ROUTES.CMS_NOTIFICATIONS;

              return {
                name: caseItemLabel(sub.name),
                href: sub.href,
                icon: sub.icon,
                isActive: isSubActive,
                badge: isNotifItem && unreadNotifCount > 0 ? (
                  <span className="ml-auto text-2xs font-semibold text-rose-500 bg-rose-500/10 px-1.5 py-0.5 rounded-full">
                    {unreadNotifCount > 9 ? '9+' : unreadNotifCount}
                  </span>
                ) : undefined,
              };
            });

            const headerBadge = planBadge && !planLoading ? (
              <span className={cn('inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold', planBadge.className)}>
                {planBadge.label}
              </span>
            ) : undefined;

            const triggerBadge = (
              <>
                {/* Collapsed plan dot indicator */}
                {planBadge && !planLoading && (
                  <span
                    className={cn(
                      'absolute top-1.5 right-1.5 h-2 w-2 rounded-full ring-2 ring-sidebar',
                      planBadge.dotColor,
                      planBadge.pulse && 'animate-pulse',
                    )}
                  />
                )}
                {/* Collapsed notifications unread badge */}
                {unreadNotifCount > 0 && !planBadge && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-2xs font-semibold leading-none text-primary-foreground">
                    {unreadNotifCount > 9 ? '9+' : unreadNotifCount}
                  </span>
                )}
              </>
            );

            return (
              <SidebarFlyout
                key={tool.name}
                title={tool.label ?? tool.name}
                icon={Icon}
                headerBadge={headerBadge}
                isActive={isActive}
                triggerBadge={triggerBadge}
                items={flyoutSubItems}
                onTriggerClick={() => {
                  if (trialNotActivated) {
                    setIsTrialModalOpen(true);
                  } else {
                    navigate(ROUTES.CMS_DASHBOARD);
                  }
                }}
                triggerClassName={cn('relative', isActive ? ICON_ACTIVE : ICON_DEFAULT)}
              />
            );
          }

          return (
            <Tooltip key={tool.href} delayDuration={0}>
              <TooltipTrigger asChild>
                <Link to={tool.href} className={isActive ? ICON_ACTIVE : ICON_DEFAULT}>
                  <Icon className="h-5 w-5 text-primary" strokeWidth={2} />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right" className="font-semibold">
                {tool.label ?? tool.name}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    );
  }

  // Expanded Mode
  return (
    <div className="pt-1">
      <span className="px-3 text-2xs font-semibold uppercase tracking-wider text-sidebar-foreground/50">
        Tools
      </span>

      <div className="mt-2 flex flex-col gap-y-1.5">
        {filteredTools.map((tool) => {
          const Icon = tool.icon;
          const isCms = tool.name === 'CMS';
          const isCmsActive = isCms && location.pathname.startsWith('/cms');
          const isActive = isCmsActive || (!isCmsActive && location.pathname.startsWith(tool.href));

          if (isCms) {
            return (
              <div key={tool.name} className="flex flex-col gap-1.5">
                <button
                  ref={isCmsActive ? cmsButtonRef : undefined}
                  onClick={() => {
                    if (trialNotActivated) {
                      // Trial not activated: clicking opens modal, not dropdown
                      setIsTrialModalOpen(true);
                    } else {
                      setCmsExpanded(!cmsExpanded);
                    }
                  }}
                  className={cn(NAV_ROW, navItemState(isCmsActive))}
                >
                  <Icon
                    className="h-5 w-5 shrink-0 text-primary transition-colors"
                    strokeWidth={isCmsActive ? 2.4 : 2}
                  />

                  <span className="text-sm font-semibold tracking-tight grow shrink-0">
                    {tool.label ?? tool.name}
                  </span>

                  {/* Plan tier badge */}
                  {planBadge && !planLoading && (
                    <span
                      className={cn(
                        'inline-flex max-w-[96px] shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-2xs font-semibold leading-none',
                        planBadge.className,
                        planBadge.pulse && 'animate-pulse',
                      )}
                    >
                      <planBadge.icon className="h-3 w-3 shrink-0" />
                      <span className="truncate">{planBadge.label}</span>
                    </span>
                  )}

                  {/* Lock icon when trial not yet activated */}
                  {trialNotActivated ? (
                    <Lock className="h-4 w-4 shrink-0 text-sidebar-foreground/50" />
                  ) : (
                    <ChevronRight
                      className={cn(
                        'h-4 w-4 shrink-0 transition-transform duration-150',
                        cmsExpanded && 'rotate-90',
                      )}
                      strokeWidth={2}
                    />
                  )}
                </button>

                {/* Only show sub-items if trial is activated */}
                {cmsExpanded && !trialNotActivated && (
                  <div className={NAV_SUB_LIST}>
                    {filterSubItems(tool.subItems).map((sub) => {
                      const SubIcon = sub.icon;
                      const isSubActive = sub.href.includes('?')
                        ? (location.pathname === sub.href.split('?')[0] && location.search.includes(sub.href.split('?')[1]))
                        : (sub.href === ROUTES.CMS_DASHBOARD
                            ? (location.pathname === ROUTES.CMS_DASHBOARD && !location.search.includes('tab=analytics'))
                            : (sub.href === ROUTES.CMS_CASE_BOARD
                                ? (location.pathname === ROUTES.CMS_CASE_BOARD)
                                : (location.pathname === sub.href || (sub.href === ROUTES.CMS_CASES && location.pathname.startsWith('/cms/cases/') && location.pathname !== ROUTES.CMS_CASE_BOARD))));
                      const isNotifItem = sub.href === ROUTES.CMS_NOTIFICATIONS;
                      return (
                        <Link
                          key={sub.href}
                          ref={isSubActive ? activeItemRef : undefined}
                          to={sub.href}
                          className={cn(NAV_SUB_ROW, navItemState(isSubActive))}
                        >
                          <SubIcon className="h-5 w-5 shrink-0 text-primary" strokeWidth={2} />
                          <span className="min-w-0 flex-1 truncate">{caseItemLabel(sub.name)}</span>
                          {isNotifItem && unreadNotifCount > 0 && (
                            <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-2xs font-semibold leading-none text-primary-foreground">
                              {unreadNotifCount > 99 ? '99+' : unreadNotifCount}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          return (
            <Link
              key={tool.href}
              to={tool.href}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-300 group/btn relative overflow-hidden w-full',
                isActive
                  ? 'bg-blue-600/20 text-blue-500 dark:text-blue-300 shadow-sm'
                  : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
              )}
            >
              {isActive && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 rounded-r-full" />
              )}

              <div
                className={cn(
                  'flex items-center justify-center h-8 w-8 rounded-lg transition-transform duration-300 group-hover/btn:scale-110',
                  isActive
                    ? 'bg-blue-600/30 text-blue-500 dark:text-blue-300'
                    : 'bg-sidebar-accent text-sidebar-foreground'
                )}
              >
                <Icon className="h-4 w-4" />
              </div>

              <span className="text-sm font-semibold tracking-tight truncate">
                {tool.label ?? tool.name}
              </span>
            </Link>
          );
        })}
      </div>

      <Dialog
        open={isTrialModalOpen}
        onOpenChange={(open) => {
          setIsTrialModalOpen(open);
          if (!open && currentPlan?.organizationId) {
            localStorage.setItem(`cms_discover_${currentPlan.organizationId}`, 'shown');
          }
        }}
      >
        <DialogContent className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-violet-500/20 bg-background p-0 shadow-2xl overflow-hidden">
          {/* Top gradient banner */}
          <div className="relative bg-gradient-to-br from-violet-600/10 via-blue-500/5 to-transparent px-6 pt-7 pb-5 text-center">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-violet-500/10 via-transparent to-transparent" />
            <div className="relative">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-500/15 ring-8 ring-violet-500/5">
                <Sparkles className="h-7 w-7 text-violet-500" />
              </div>
              <DialogTitle className="text-xl font-bold text-foreground tracking-tight">
                BlueB Case Management System
              </DialogTitle>
              <DialogDescription className="mt-1.5 text-sm text-muted-foreground max-w-xs mx-auto">
                Your all-in-one platform for security operations — alert triage, investigation, and team collaboration.
              </DialogDescription>
            </div>
          </div>

          {/* Feature highlights */}
          <div className="px-6 py-4">
            <div className="grid grid-cols-2 gap-2 mb-5">
              {[
                { icon: AlertTriangle, label: 'Alert Triage Queue',   color: 'text-orange-500 bg-orange-500/10' },
                { icon: FolderOpen,   label: 'Case Investigations',    color: 'text-blue-500 bg-blue-500/10' },
                { icon: Crosshair,   label: 'IOC & Threat Intel',      color: 'text-red-500 bg-red-500/10' },
                { icon: Users,       label: 'Team Collaboration',      color: 'text-green-500 bg-green-500/10' },
                { icon: BarChart2,   label: 'Analyst Metrics',         color: 'text-violet-500 bg-violet-500/10' },
                { icon: ShieldCheck, label: 'CERT Collaboration',      color: 'text-amber-500 bg-amber-500/10' },
              ].map(({ icon: Icon, label, color }) => (
                <div key={label} className="flex items-center gap-2 rounded-lg border border-border/50 bg-muted/20 px-2.5 py-2">
                  <div className={cn('flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md', color)}>
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <span className="text-xs font-medium text-foreground leading-tight">{label}</span>
                </div>
              ))}
            </div>

            {/* CTA buttons */}
            <div className="space-y-2">
              <Button
                className="w-full bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 text-white font-bold rounded-xl h-11 shadow-lg shadow-violet-500/20 border-0 gap-2"
                disabled={activatingTrial}
                onClick={handleActivateTrial}
              >
                {activatingTrial ? (
                  <span className="flex items-center gap-2">
                    <Timer className="h-4 w-4 animate-spin" />
                    Starting trial...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Star className="h-4 w-4" />
                    Start Free 14-Day Trial
                  </span>
                )}
              </Button>
              <Button
                variant="outline"
                className="w-full font-semibold rounded-xl h-10 text-sm border-border/60 gap-2"
                onClick={() => {
                  setIsTrialModalOpen(false);
                  // Use setTimeout to ensure modal closes before navigation
                  setTimeout(() => window.location.assign(ROUTES.CMS_SETTINGS), 100);
                }}
              >
                <Crown className="h-3.5 w-3.5" />
                Skip Trial — View Paid Plans
              </Button>
              <button
                className="w-full text-center text-xs text-muted-foreground hover:text-foreground py-1 transition-colors"
                onClick={() => setIsTrialModalOpen(false)}
              >
                Remind me later
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}